import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { LocalizedMessage, LocalizedStandardDateNoSeconds } from 'intl/LocalizedComponents';
import _get from 'lodash/get';
import _has from 'lodash/has';
import PropTypes from 'prop-types';
// Permissions
import permissions from 'constants/Permissions';
import OnlyWith from 'components/permissions/OnlyWith';
// HOCs
import withFormManagement from 'hocs/WithFormManagement';
import withPermissions from 'hocs/WithPermissions';
import withStateReinit from 'hocs/WithStateReinit';
// Core components
import { ContainedButton } from 'components/core/Button';
import Card from 'components/core/Card';
import Typography from 'components/core/Typography';
// Custom
import { cpmOnDemandAccessDataShape, errorShape } from 'constants/PropTypesShapes';
import { openDialog, closeDialog } from 'actions';
import { buildFormErrors, completeFormErrors, cpmParkingCategorizeConstraints } from 'utils/Forms';
import { dateGenerator } from 'utils/DateUtils';
import FormDialogTemplate from 'components/templates/FormDialogTemplate';
import FormHelper from 'components/misc/FormHelper';
import Callout from 'components/misc/Callout';
import { DateEntryItem } from 'components/items/InformationItems';
import { DeleteIconButton } from 'components/buttons/IconButtons';
import InformationItem from 'components/items/InformationItem';
import ParkingCategorySelectField from 'cpmCarparks/assets/parkingCategories/ParkingCategorySelectField';
import CustomerItem from 'cpmCarparks/assets/customers/CustomerItem';

class OnDemandCategorizeForm extends React.Component {
  constructor(props) {
    super(props);

    this.handleChooseCustomer = this.handleChooseCustomer.bind(this);
    this.handleValidateChooseCustomer = this.handleValidateChooseCustomer.bind(this);
    this.handleValidate = this.handleValidate.bind(this);
  }

  componentDidMount() {
    // We need to pass component reference to parent (= DialogItem)
    const { onRef } = this.props;
    onRef(this);
  }

  // Handlers
  handleChooseCustomer() {
    const { id, timezone, handleOpenDialog } = this.props;

    handleOpenDialog({
      title: {
        id: 'title.chooseCarparkUser',
      },
      actions: [{ type: 'close' }],
      component: {
        type: 'CARPARK_CUSTOMER_CHOOSE',
        props: {
          id,
          timezone,
          withOnDemandCategory: false,
          withChoose: true,
          fullWidth: false,
          permanentFiltersSpecs: [{ key: 'onDemand', value: false }],
          onSelectCustomer: this.handleValidateChooseCustomer,
        },
      },
      dividers: true,
      size: 'md',
    });
  }

  handleValidateChooseCustomer(customer) {
    const { onFieldsChange, handleCloseDialog } = this.props;
    // Set customer
    onFieldsChange({
      customer,
      parkingCategoryId: null,
    });
    // Close choose list
    handleCloseDialog('CARPARK_CUSTOMER_CHOOSE');
  }

  // Handlers called by parent (= DialogItem)
  handleValidate() {
    const {
      onDemand,
      fields: { parkingCategoryId, customer },
      onErrorsChange,
      onValidate,
      hasPermission,
    } = this.props;

    // Permissions
    const {
      pCpm: { parkingCategoryRead },
    } = permissions;

    let errors = buildFormErrors({}, cpmParkingCategorizeConstraints);
    // Check that either a parking category or a customer is defined
    if (hasPermission({ needed: parkingCategoryRead, permissionsOrContext: 'carparkPermissions' }) && !parkingCategoryId && !customer) {
      errors = completeFormErrors(errors, 'parkingCategorizationFields', ['mustSelectParkingCategoryOrCustomer']);
    } else if (!hasPermission({ needed: parkingCategoryRead, permissionsOrContext: 'carparkPermissions' }) && !parkingCategoryId) {
      errors = completeFormErrors(errors, 'parkingCategorizationFields', ['mustSelectParkingCategory']);
    }
    // Apply errors
    onErrorsChange(errors);

    // No error => validate form
    if (!errors) {
      onValidate(onDemand, {
        customerId: customer ? customer.id : null,
        parkingCategoryId,
      });
      return true;
    }

    return false;
  }

  render() {
    const {
      onDemand,
      timezone,
      fields: { parkingCategoryId, customer },
      errors,
      onFieldsChange,
      hasPermission,
    } = this.props;

    // Permissions
    const {
      pCpm: { parkingCategoryRead },
    } = permissions;

    // Help elements
    const categorizationHelperElements = [];
    if (hasPermission({ needed: parkingCategoryRead, permissionsOrContext: 'carparkPermissions' })) {
      categorizationHelperElements.push(
        {
          title: <LocalizedMessage id="field.categoryOrUser" />,
          content: <LocalizedMessage id="help.onDemand.formParkingCategory" />,
        },
        {
          content: <LocalizedMessage id="info.orUpper" />,
        },
        {
          content: <LocalizedMessage id="help.onDemand.formUser" />,
        },
      );
    } else {
      categorizationHelperElements.push({
        title: <LocalizedMessage id="field.carparkUser" />,
        content: <LocalizedMessage id="help.onDemand.formUser" />,
      });
    }

    return (
      <FormDialogTemplate errors={errors}>
        <div className="row information-item-wrapper">
          <div className="col-12 col-md-6">
            <DateEntryItem
              lightMode
              value={
                <LocalizedStandardDateNoSeconds value={dateGenerator.dateFromTimezone(onDemand.begin, timezone)} />
              }
            />
          </div>
          <div className="col-12 col-md-6">
            <InformationItem
              lightMode
              icon="cAwTypeLPlate"
              label={<LocalizedMessage id="field.licensePlate" />}
              value={_get(onDemand, 'licensePlate')}
            />
          </div>
          <div className="col-12 pt-3">
            <div className="d-flex">
              <FormHelper elements={categorizationHelperElements} />
              <div style={{ flex: 1 }}>
                <Card>
                  <div className="row">
                    {_has(errors, 'parkingCategorizationFields') && (
                      <div className="col-12 pb-3">
                        <Callout severity="error" withInfoIcon>
                          {errors.parkingCategorizationFields}
                        </Callout>
                      </div>
                    )}
                    <OnlyWith
                      permissions={{
                        needed: parkingCategoryRead,
                        permissionsOrContext: 'carparkPermissions',
                      }}
                    >
                      <React.Fragment>
                        <div className="col-12">
                          <Typography type="subtitle2">
                            <LocalizedMessage id="field.parkingCategory" />
                          </Typography>
                        </div>
                        <div className="col-12 col-lg-8 pb-4">
                          <ParkingCategorySelectField
                            withOnDemand={false}
                            errorText={_get(errors, 'parkingCategoryId')}
                            value={parkingCategoryId}
                            required={!customer}
                            disabled={!!customer}
                            onChange={(value) => onFieldsChange({ parkingCategoryId: value })}
                          />
                        </div>
                      </React.Fragment>
                    </OnlyWith>
                    <div className="col-12 col-lg-6">
                      <Typography type="subtitle2">
                        <LocalizedMessage id="field.carparkUser" />
                      </Typography>
                    </div>
                    <div className="col-12 col-lg-6 pt-2 pt-lg-0 d-flex justify-content-end">
                      <ContainedButton
                        size="small"
                        label={<LocalizedMessage id="button.chooseUser" />}
                        iconId="btnUsers"
                        onClick={this.handleChooseCustomer}
                      />
                    </div>
                    <div className="col-12 pt-3">
                      {customer ? (
                        <div className="information-item-wrapper">
                          <CustomerItem
                            customer={customer}
                            rightButton={<DeleteIconButton onClick={() => onFieldsChange({ customer: null })} />}
                          />
                        </div>
                      ) : (
                        <em>
                          <LocalizedMessage id="info.noUserSelected" />
                        </em>
                      )}
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </FormDialogTemplate>
    );
  }
}

OnDemandCategorizeForm.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onDemand: cpmOnDemandAccessDataShape.isRequired,
  timezone: PropTypes.string.isRequired,
  operationError: errorShape, // eslint-disable-line react/no-unused-prop-types
  fields: PropTypes.object.isRequired,
  errors: PropTypes.object,
  onFieldsChange: PropTypes.func.isRequired,
  onErrorsChange: PropTypes.func.isRequired,
  onRef: PropTypes.func.isRequired,
  onValidate: PropTypes.func.isRequired,
  handleOpenDialog: PropTypes.func.isRequired,
  handleCloseDialog: PropTypes.func.isRequired,
  hasPermission: PropTypes.func.isRequired,
};

OnDemandCategorizeForm.defaultProps = {
  operationError: null,
  errors: null,
};

const mapFieldsToProps = {
  parkingCategoryId: {
    defaultValue: null,
  },
  customer: {
    defaultValue: null,
  },
};

const mapOwnPropsToResources = (ownProps) => ({
  resourceData: ownProps.onDemand,
  operationError: ownProps.operationError,
});

const mapStateToProps = (state) => ({
  operationError: state.cpmCarparkOnDemand.categorize.error,
});

const mapDispatchToProps = (dispatch) => ({
  handleOpenDialog: (props) => {
    dispatch(openDialog(props));
  },
  handleCloseDialog: (componentType) => {
    dispatch(closeDialog(componentType));
  },
});

module.exports = compose(
  connect(mapStateToProps, mapDispatchToProps),
  withFormManagement(mapFieldsToProps, mapOwnPropsToResources, false),
  withPermissions,
  withStateReinit(['categorizeCarparkOnDemand']),
)(OnDemandCategorizeForm);



// WEBPACK FOOTER //
// ./routes/cpm/routes/carparks/assets/onDemand/OnDemandCategorizeForm.js